import { mapValues } from 'lodash'

import {
	type BaseStep,
	type GetExtendedStateRef,
	type StepHandlerResult,
	type StepMetaHistoryClear,
	StepMetaType,
} from '@subaio/components'
import { isPromise } from '@subaio/utils'

type Options<TStep extends BaseStep> = {
	handlers: Record<string, (...args: Array<any>) => Awaitable<StepHandlerResult<TStep>>>
	getState: () => { stack: Array<TStep>; isLoading: boolean }
	setState: (state: { stack: Array<TStep>; isLoading: boolean }) => void
	getExtendedStateRef: GetExtendedStateRef<TStep> & { current: any }
	onError: (error: any) => void
	onBack: () => void
}

/**
 * Modified step-handler wrapping for use inside the Main component only. Handles stacks, back-navigation and extending
 * steps.
 */
export const wrapStepHandlers = <TStep extends BaseStep>({
	handlers,
	getState,
	setState,
	getExtendedStateRef,
	onError,
	onBack,
}: Options<TStep>) =>
	mapValues(handlers, (func) => (...args: Array<any>) => {
		try {
			const { stack, isLoading } = getState()
			if (isLoading) {
				throw new Error('Cannot run simultaneous step handlers')
			}
			const [current, ...restStack] = stack
			const currentExtended = getExtendedStateRef.current?.(current) ?? current
			const result = func(...args)

			if (!isPromise(result)) {
				handleStep(result, currentExtended, restStack, setState, onBack)
				return
			}

			setState({ isLoading: true, stack })
			return result.then(
				(step) => {
					const state = getState()
					if (state.isLoading) handleStep(step, currentExtended, restStack, setState, onBack)
				},
				(error) => {
					setState({ isLoading: false, stack })
					onError(error)
				},
			)
		} catch (error) {
			onError(error)
		}
	})

/**
 * Handle meta steps when setting values
 */
const handleStep = <TStep extends BaseStep>(
	step: StepHandlerResult<TStep>,
	currentExtended: TStep,
	restStack: Array<TStep>,
	setState: (state: { stack: Array<TStep>; isLoading: boolean }) => void,
	onBack: () => void,
) => {
	switch (step.type) {
		case StepMetaType.HistoryClear:
			setState({ stack: [(step as StepMetaHistoryClear<TStep>).step], isLoading: false })
			break
		case StepMetaType.CloseView:
			onBack()
			break
		default:
			setState({ stack: [step as TStep, currentExtended, ...restStack], isLoading: false })
	}
}
